import { Injectable } from '@angular/core';
import { FilterImportResults } from '../models/filter-import.models';
import { Filter, StoredFilter } from '../models/filter-models';
import { patchVersion } from '../../version';
import { FilterMigratorService } from './filter-migrator.service';
import { VERSION } from '../const';

@Injectable({
  providedIn: 'root'
})
export class FilterImporterService {
  constructor(private filterMigratorService: FilterMigratorService) {}

  importFilter(filterContent: string): FilterImportResults {
    const importResult = new FilterImportResults();

    try {
      const versionMatch = filterContent.match(/# Version: (.+)/);
      const gameMatch = filterContent.match(/# Game: (.+)/);
      const dateMatch = filterContent.match(/# Export date: (.+)/);
      const configMatch = filterContent.match(/# \[\[Config Start\]\]\r?\n([\s\S]*?)\r?\n# \[\[Config End\]\]/);

      if (versionMatch && dateMatch && configMatch) {
        importResult.importFilterExportVersion = versionMatch[1];
        importResult.importFilterExportGame = gameMatch?.[1] || 'PoE2';
        importResult.importFilterExportDate = new Date(dateMatch[1]).toString();
        const config = configMatch[1].replace(/^# /gm, '');
        // Parse patchVersion from version string (handles both old "0.9.132" and new "1.0.132.1" formats)
        const importedBuildNumber = importResult.importFilterExportVersion ? (() => {
          const parts = importResult.importFilterExportVersion.split('.');
          // If 4 parts (new format), use 3rd part (patchVersion). If 3 parts (old format), use last part.
          return Number(parts.length === 4 ? parts[2] : parts[parts.length - 1] || '0');
        })() : 0;

        try {
          importResult.filter = JSON.parse(config);
          try {
            if (importedBuildNumber < patchVersion) {
              // TODO: support for poe2 <-> poe1?
              this.filterMigratorService.migrateFilter(importResult.filter!, importedBuildNumber, 'import');
              importResult.importFilterMigratedFrom = importResult.importFilterExportVersion;
              importResult.importFilterMigratedTo = VERSION;
            }
            importResult.importFilterSuccess = true;
          } catch (error: any) {
            importResult.importFilterError = 'Error migrating filter: ' + error;
          }
        } catch (error: any) {
          importResult.importFilterError = error;
        }
      } else {
        importResult.importFilterError = 'Missing version, date, or filter configuration in the imported file.';
      }
    } catch (error: any) {
      importResult.importFilterError = error;
    }

    return importResult;
  }

  importFilterJson(jsonContent: string): FilterImportResults {
    const importResult = new FilterImportResults();

    try {
      const parsed = JSON.parse(jsonContent.trim());
      let filter: Filter;
      let importedBuild = patchVersion;

      if (this.isStoredFilterJson(parsed)) {
        const storedFilter = parsed as StoredFilter;
        filter = storedFilter.filter;
        importedBuild = storedFilter.build ?? 0;
        importResult.importedName = storedFilter.name;
        importResult.importFilterExportVersion = storedFilter.version ?? null;
      } else {
        filter = parsed as Filter;
        const lastMigration = filter.migrationHistory?.at(-1);
        if (lastMigration?.newVersion) {
          importedBuild = Number(lastMigration.newVersion);
        }
      }

      try {
        if (importedBuild < patchVersion) {
          this.filterMigratorService.migrateFilter(filter, importedBuild, 'import');
          importResult.importFilterMigratedFrom = importedBuild.toString();
          importResult.importFilterMigratedTo = VERSION;
        }
        importResult.filter = filter;
        importResult.importFilterSuccess = true;
      } catch (error: any) {
        importResult.importFilterError = 'Error migrating filter: ' + error;
      }
    } catch (error: any) {
      importResult.importFilterError = error?.toString() || 'Invalid filter JSON';
    }

    return importResult;
  }

  private isStoredFilterJson(parsed: unknown): parsed is StoredFilter {
    return !!parsed
      && typeof parsed === 'object'
      && 'filter' in parsed
      && typeof (parsed as StoredFilter).filter === 'object';
  }
}
